import React, { memo, useEffect, useMemo, useState } from 'react'
import styles from './FinancialInformation.module.scss'
import CommonFormFieldWrapper from '../../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../../stores/store'
import { FinancialInformationSelector, bvnInitializeThunk, bvnVerifyThunk, bvnDetailsThunk, saveMonoThunk, InitiateLookupDataMethods, personalInformationSelector } from '../../../../stores/slices/customer-registration-slice'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import CommonPhoneInput from '../../../../common/components/phone-input/CommonPhoneInput'
import Tab from '../../../../common/components/tab-viewer/Tab'
import MonoConnect from '../../../../common/components/mono-connect/MonoConnect'
import { hideLoader, showLoader } from '../../../../stores/slices/loader-slice'
import { useCreateAnOrderMutation } from '../../../../service/order-service'
import { buySolarProductSelector } from '../../../../stores/slices/buy-solar-product-slice'
import { ApplicationRoutes } from '../../../../routes'
import CustomerSignUpFormWrapper from '../../../sign-up/wrapper/CustomerSignUpFormWrapper'
import Input from '../../../../common/components/new/ui/Input'
import { EmailIcon, PhoneIcon, ProfileIcon, VerifyIcon } from '../../../../assets/icons'
import { useForm } from 'react-hook-form'
import VerifyOtpStepBVN from '../../../sign-up/VerifyOtpStepBVN'
import RadioButtonArea from '../../../../common/components/new/radio-button-area/RadioButtonArea'
import BvnOptionArea from '../../../../common/components/new/bvn-option-area/BvnOptionArea'

type Props = {
    stepNumber: number
    onBack: (index: number) => void
    onNext: (index: number) => void
}

const FinancialInformation: React.FC<Props> = memo((props: Props) => {
    const dispatch = useDispatch<AppDispatch>();

    const financialInformation = useSelector(FinancialInformationSelector);
    const personalInformation = useSelector(personalInformationSelector);

    const [ display, setDisplay ] = useState(1);
    
    const navigate = useNavigate();

    const [ createAnOrder, _ ] = useCreateAnOrderMutation();

    const cartDetails = useSelector(buySolarProductSelector);
    
    const [ agreeToTerms, setAgreeToTerms ] = useState(false);

    // Form Data for Redux
    const [ scope ] = useState(financialInformation?.scope ?? 'identity');
    const [ isBvnVerified, setIsBvnVerified ] = useState(financialInformation?.bvnVerified ?? false);
    const [ methods, setMethods ] = useState<InitiateLookupDataMethods[]>(financialInformation?.methods ?? []);
    const [ selectedMethodNumber, setSelectedMethodNumber ] = useState(financialInformation.selectedMethodNumber ?? -1);
    const [ selectedMethodName, setSelectedMethodName ] = useState(financialInformation.selectedMethodName ?? '');
    const [ alternatePhoneNumber, setAlternatePhoneNumber ] = useState(financialInformation.alternatePhoneNumber ?? '');
    const [ isMonoVerified, setIsMonoVerified ] = useState(financialInformation.monoVerified ?? false);
    const [ isOtpCodeSent, setIsOtpCodeSent ] = useState(false);
    
    // Other Data
    const [ otp, setOtp ] = useState('');
    const [ sessionId, setSessionId ] = useState(financialInformation.sessionId ?? '');

    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = useForm<{
        scope: string,
        bvn: string,
        isBvnVerified: boolean,
        selectedMethodNumber?: number,
        selectedMethodName?: string,
        alternatePhoneNumber?: string,
        isMonoVerified?: boolean,
        isOtpCodeSent?: boolean,
        otp?: string,
        sessionId?: string
    }>({
        reValidateMode: 'onSubmit',
        mode: 'onBlur'
    })


    useEffect(() => {
        setValue('scope', financialInformation.scope ?? '')
        setValue('bvn', financialInformation.bvn ?? '')
        setValue('isBvnVerified', financialInformation.bvnVerified ?? false)
        setValue('selectedMethodNumber', financialInformation.selectedMethodNumber)
        setValue('selectedMethodName', financialInformation.selectedMethodName)
        setValue('alternatePhoneNumber', financialInformation.alternatePhoneNumber)
        setValue('isMonoVerified', financialInformation.monoVerified)
        setValue('isOtpCodeSent', false)
        setValue('otp', '')
        setValue('sessionId', financialInformation.sessionId ?? '')
    }, [financialInformation, setValue])

    useEffect(() =>{
        setIsBvnVerified(financialInformation.bvnVerified!)
        setIsMonoVerified(financialInformation.monoVerified!)
        setSessionId(financialInformation.sessionId!)
    }, [financialInformation.bvnVerified, financialInformation.monoVerified, financialInformation.sessionId])

    const onVerifyBVN = async () => {
        try {
            dispatch(showLoader())
            const result = await dispatch(bvnInitializeThunk({
                bvn: watch('bvn'),
                scope: scope,
            }))
    
            if (bvnInitializeThunk.fulfilled.match(result)) {
                setMethods(result.payload.methods!)

                setIsOtpCodeSent(false)
                toast.success('BVN Initialization was successful')
                changeDisplay(display + 1)
            } else if (bvnInitializeThunk.rejected.match(result)) {
                toast.error(result.payload as string)
            }
        } finally {
            dispatch(hideLoader())
        }
    }

    const onChooseVerifyBVNMethod = async () => {
        // console.log({
        //     method: selectedMethodName,
        //     methodNumber: selectedMethodNumber,
        //     phoneNumber: alternatePhoneNumber,
        //     sessionId: sessionId,
        // })
        // changeDisplay(display + 1)
        // return;

        try {
            dispatch(showLoader())
            const result = await dispatch(bvnVerifyThunk({
                method: selectedMethodName,
                methodNumber: selectedMethodNumber,
                phoneNumber: alternatePhoneNumber,
                sessionId: sessionId,
            }))
    
            if (bvnVerifyThunk.fulfilled.match(result)) {
                setIsOtpCodeSent(true);
                changeDisplay(display + 1)
                toast.info('You have been sent OTP code.')
            } else if (bvnVerifyThunk.rejected.match(result)) {
                toast.error(result.payload as string)
            }
        } finally {
            dispatch(hideLoader())
        }

        
    }

    const onEnterCode = async (otp: string) => {
        // console.log({
        //     otp: otp,
        //     sessionId: sessionId,
        // })
        // changeDisplay(display + 1);
        // return;

        try {
            dispatch(showLoader())
            const result = await dispatch(bvnDetailsThunk({
                otp: otp,
                sessionId: sessionId,
            }))
    
            if (bvnDetailsThunk.fulfilled.match(result)) {
                toast.success('Successfully obtained BVN Details')
                changeDisplay(display + 1)
            } else if (bvnDetailsThunk.rejected.match(result)) {
                toast.error(result.payload as string)
            }
        } finally {
            dispatch(hideLoader())
        }
    }

    const onMonoVerification = async (monoTemporaryToken: string) => {
        // console.log({
        //     monoTemporaryToken: monoTemporaryToken,
        // })
        // return;
        
        try {
            const result = await dispatch(saveMonoThunk({
                monoTemporaryToken: monoTemporaryToken,
            }))
    
            if (bvnDetailsThunk.fulfilled.match(result)) {
                setIsMonoVerified(true);
                toast.success('Successfully obtained BVN Details')
            } else if (bvnDetailsThunk.rejected.match(result)) {
                setIsMonoVerified(false);
                toast.error(result.payload as string)
            }
        } finally {
            dispatch(hideLoader())
        } 
        
    }

    const onFinished = async () => {
        try {
            await createAnOrder({
                productId: cartDetails.productDetailsId!,                
                durationOfPaymentsInMonths: cartDetails.durationOfPaymentsInMonths!,
                roofType: cartDetails.roofType!,
                numberOfFloors: cartDetails.numberOfFloors!
            }).unwrap()

            toast.success('Order created successfully')
            navigate(`/${ApplicationRoutes.CUSTOMER_BASE}/${ApplicationRoutes.DASHBOARD}`)
        } catch (error: any) {
			toast.error(error.data?.title || "Something went wrong. Please try again.");
		}
        
    }

    const registeredInputs = useMemo(() => {
		return {
            scope: register("scope"),
            bvn: register("bvn", { required: "BVN is required" }),
            isBvnVerified: register("isBvnVerified"),
            selectedMethodNumber: register("selectedMethodNumber"),
            selectedMethodName: register("selectedMethodName"),
            alternatePhoneNumber: register("alternatePhoneNumber"),
            isMonoVerified: register("isMonoVerified"),
            isOtpCodeSent: register("isOtpCodeSent"),
            otp: register("otp"),
            sessionId: register("sessionId"),
		};
	}, [register, watch]);

    const selectMethod = (method: InitiateLookupDataMethods) => {
        setSelectedMethodNumber(methods.findIndex(x => x === method));
        setSelectedMethodName(method.method!);
    }

    const changeDisplay = (n: number) => {
        if (n === 5) props.onNext(props.stepNumber + 1);
        else if (n === 0) props.onBack(props.stepNumber - 1);
        else setDisplay(n);
    }



    return (
        <>
            {
                display == 1 &&
                <CustomerSignUpFormWrapper
                    onBack={() => changeDisplay(display - 1)}
                    onNext={() => changeDisplay(display + 1)}
                    nextButtonDisabled={false /*!isBvnVerified */}
                    labelBackStep='Identity'
                    title='Verification Information'
                    subtitle='Verify Financial Information'
                >
                    <div className={styles.container}>
                        <Input
                            ref={registeredInputs.bvn.ref}
                            label="Enter BVN"
                            placeholder="Type your BVN"
                            required
                            error={errors.bvn?.message}
                            inputProps={registeredInputs.bvn}
                            button={{
                                label: 'Verify BVN',
                                verified: isBvnVerified,
                                function: onVerifyBVN,
                            }}
                        >
                            <VerifyIcon />
                        </Input>
                        <MonoConnect key={9999} checked={agreeToTerms} onChecked={value => setAgreeToTerms(value)} onRetrievedMonoToken={(monoTemporaryToken: string) => onMonoVerification(monoTemporaryToken)}
                        isBvnVerified={isBvnVerified}
                        isMonoVerified={isMonoVerified}
                        customerDetails={{
                            email: personalInformation.email!,
                            firstName: personalInformation.firstName!,
                            lastName: personalInformation.lastName!,
                            bvn: financialInformation.bvn!
                        }} 
                        />
                    </div>
                </CustomerSignUpFormWrapper>
            }
            {
                display == 2 &&
                <CustomerSignUpFormWrapper
                    labelNext={isOtpCodeSent ? 'Verify your BVN again to get another OTP' : 'Send OTP'}
                    onBack={() => changeDisplay(display - 1)}
                    onNext={onChooseVerifyBVNMethod}
                    title='Select Verification Option'
                    subtitle='Please choose the method you want to use to validate your BVN Details'
                >
                    <div className={styles.container}>
                        <BvnOptionArea onChange={selectMethod} methods={methods} initialValue={selectedMethodName} onPhoneNumberChange={value => setValue("alternatePhoneNumber", value)}  />
                    </div>
                </CustomerSignUpFormWrapper>
            }
            {
                display == 3 &&
                <VerifyOtpStepBVN 
                    title='OTP Verification'
                    subtitle='Please verify your OTP to proceed'
                    getOtp={onEnterCode} 
                    getBack={() => setDisplay(display - 1)} />
            }
            {
                display == 4 &&
                <CustomerSignUpFormWrapper
                    onBack={() => changeDisplay(display - 1)}
                    onNext={() => changeDisplay(display + 1)}
                    title='Verification Information'
                    subtitle='Verify Financial Information'
                >
                    <div className={styles.container}>
                        <Input
                            ref={registeredInputs.bvn.ref}
                            label="Enter BVN"
                            placeholder="Type your BVN"
                            error={errors.bvn?.message}
                            inputProps={registeredInputs.bvn}
                            button={{
                                label: 'Verify BVN',
                                verified: isBvnVerified,
                                function: onVerifyBVN,
                            }}
                        >
                            <ProfileIcon />
                        </Input>
                        <MonoConnect key={9999} checked={agreeToTerms} onChecked={value => setAgreeToTerms(value)} onRetrievedMonoToken={(monoTemporaryToken: string) => onMonoVerification(monoTemporaryToken)}
                        isBvnVerified={isBvnVerified}
                        isMonoVerified={isMonoVerified}
                        customerDetails={{
                            email: personalInformation.email!,
                            firstName: personalInformation.firstName!,
                            lastName: personalInformation.lastName!,
                            bvn: financialInformation.bvn!
                        }} 
                        />
                    </div>
                </CustomerSignUpFormWrapper>
            }
        </>
    )
})

export default FinancialInformation